import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isObjectNotEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from '../utils/objectUtils';
import { createWithId, getById, remove, update, updateField, updateFields } from '../utils/dbUtils';
import PromisePool from '@supercharge/promise-pool/dist';
import { sendTemplateEmail } from '../firebase/Email';
import Lead from './Lead';
import Activity from './Activity';
import CONSTANTS from '../constants';
import User from './User';
import { db } from '../firebase';
import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from 'firebase/firestore';
import Order from './Order';
import Subscription from './Subscription';
import Program from './Program';
import { toUpper } from 'lodash';
import OrderGroup from './OrderGroup';
import { buildLeadFunnelLink } from '../utils/offerLinkUtils';

const getBaseTestContact = (toEmail = 'test@example.com') => {
	return {
		userId: 'user_test_123',
		firstName: 'Test',
		lastName: 'Patient',
		email: toEmail,
		phone: '+15551234567',
		patientId: 'P-TEST-1001',
		dateOfBirth: '1990-01-15',
		gender: 'Female',
		city: 'Miami',
		state: 'FL',
	};
};

const getBaseTestOtherInfo = () => {
	return {
		userId: 'user_test_123',
		coveredEntityId: 'ALL',
		leadId: 'lead_test_123',
		orderId: 'order_test_123',
		orderNumber: 'RRX-1001',
		groupId: 'group_test_123',
		subscriptionId: 'sub_test_123',
		encounterId: 'enc_test_123',
		checkInId: 'checkin_test_123',
		rxId: 'RX-TEST-1001',
		prescriptionId: 'RX-TEST-1001',
		total: '99.00',
		subtotal: '89.00',
		amount: '99.00',
		balance: '0.00',
		refund: '10.00',
		paymentFrequency: 'Monthly',
		paymentMethod: 'Credit Card ending in 4242',
		productName: 'Semaglutide Starter Kit',
		productImageUrl: 'https://app.readyrx.com/logo192.png',
		productSku: 'SEMAGLUTIDE-G',
		shippingAddress: '123 Test St, Miami, FL 33101 USA',
		billingAddress: '123 Test St, Miami, FL 33101 USA',
		trackingNumber: '1Z999AA10123456784',
		carrier: 'UPS',
		status: 'ACTIVE',
		billingStatus: 'PAID',
		appointmentDate: 'April 5, 2026 at 10:30 AM ET',
		encounterDate: 'April 5, 2026 at 10:30 AM ET',
		checkInDate: 'April 12, 2026',
		nextPaymentDate: 'April 19, 2026',
		refillDate: 'April 19, 2026',
		providerFirstName: 'Taylor',
		providerLastName: 'Smith',
		doctorFirstName: 'Taylor',
		doctorLastName: 'Smith',
		doctorName: 'Dr. Taylor Smith',
		practiceName: 'ReadyRx Test Practice',
		locationOfService: 'Telehealth',
		programName: 'Weight Loss',
		programSlug: 'wl',
		couponCode: 'TEST10',
		message: 'This is a sample message used for template testing.',
		link: 'https://app.readyrx.com/test-email',
	};
};

const getTestPayloadByType = (type, toEmail) => {
	const contact = getBaseTestContact(toEmail);
	const baseOtherInfo = getBaseTestOtherInfo();

	switch (type) {
		case 'LEAD':
			return {
				contact,
				otherInfo: {
					...baseOtherInfo,
					status: 'APPROVED',
					link: 'https://app.readyrx.com/f/wl/se?lid=lead_test_123&coupon=TEST10',
				},
			};
		case 'ORDER':
			return {
				contact,
				otherInfo: {
					...baseOtherInfo,
					status: 'SHIPMENT_SHIPPED',
					link: `https://www.ups.com/track?loc=en_US&tracknum=${baseOtherInfo.trackingNumber}`,
				},
			};
		case 'ORDER_GROUP':
			return {
				contact,
				otherInfo: {
					...baseOtherInfo,
					status: 'PROCESSING',
					link: 'https://app.readyrx.com/account/settings/orders',
				},
			};
		case 'SUBSCRIPTION':
			return {
				contact,
				otherInfo: {
					...baseOtherInfo,
					status: 'ACTIVE',
					link: 'https://app.readyrx.com/account/settings/subscriptions',
				},
			};
		case 'ENCOUNTER':
			return {
				contact,
				otherInfo: {
					...baseOtherInfo,
					status: 'SCHEDULED',
					link: 'https://app.readyrx.com/e/enc_test_123',
				},
			};
		case 'CHECK_IN':
			return {
				contact,
				otherInfo: {
					...baseOtherInfo,
					status: 'COMPLETED',
					link: 'https://app.readyrx.com/my-check-ins',
				},
			};
		case 'USER':
		default:
			return {
				contact,
				otherInfo: {
					...baseOtherInfo,
					link: 'https://app.readyrx.com/account/settings',
				},
			};
	}
};

class EmailTemplate {
	static collectionName = 'emailTemplates';

	id = null;
	coveredEntityId = null;
	name = null;
	subject = null;
	header = null;
	message = null;
	fromEmail = 'support@readyrx.com';
	fromName = 'ReadyRx';
	replyToEmail = 'support@readyrx.com';
	replyToName = 'ReadyRx Customer Support Team';
	sendMultiple = false; // whether or not this template is allowed to be sent to the user or lead more than once. whether the sentEmails array should be checked.
	type = null; // LEAD, USER, ORDER, SUBSCRIPTION
	marketing = false; // true = marketing, false = transactional email
	categories = [];

	constructor(emailTemplate) {
		if (isObjectNotEmpty(emailTemplate)) {
			if (isStringNotEmpty(emailTemplate.id)) { this.id = emailTemplate.id; }
			if (isStringNotEmpty(emailTemplate.coveredEntityId)) { this.coveredEntityId = emailTemplate.coveredEntityId; }
			if (isStringNotEmpty(emailTemplate.name)) { this.name = emailTemplate.name; }
			if (isStringNotEmpty(emailTemplate.subject)) { this.subject = emailTemplate.subject; }
			if (isStringNotEmpty(emailTemplate.header)) { this.header = emailTemplate.header; }
			if (isStringNotEmpty(emailTemplate.message)) { this.message = emailTemplate.message; }
			if (isStringNotEmpty(emailTemplate.fromEmail)) { this.fromEmail = emailTemplate.fromEmail; }
			if (isStringNotEmpty(emailTemplate.fromName)) { this.fromName = emailTemplate.fromName; }
			if (isStringNotEmpty(emailTemplate.replyToEmail)) { this.replyToEmail = emailTemplate.replyToEmail; }
			if (isStringNotEmpty(emailTemplate.replyToName)) { this.replyToName = emailTemplate.replyToName; }
			if (isBooleanTrue(emailTemplate.sendMultiple)) { this.sendMultiple = true; }
			if (isStringNotEmpty(emailTemplate.type)) { this.type = emailTemplate.type; }
			if (isBooleanTrue(emailTemplate.marketing)) { this.marketing = true; }
			if (isListNotEmpty(emailTemplate.categories)) { this.categories = emailTemplate.categories; }
		}
	}

	toDto = () => {
		return {
			id: this.id,
			coveredEntityId: this.coveredEntityId,
			name: this.name,
			subject: this.subject,
			header: this.header,
			message: this.message,
			fromEmail: this.fromEmail,
			fromName: this.fromName,
			replyToEmail: this.replyToEmail,
			replyToName: this.replyToName,
			sendMultiple: this.sendMultiple,
			type: this.type,
			marketing: this.marketing,
			categories: this.categories,
		};
	}

	create = async () => {
		return await createWithId(EmailTemplate.collectionName, this.id, this.toDto());
	}

	static updateField = async (id, fieldName, newValue) => {
		if (isStringNotEmpty(id)) {
			return await updateField(EmailTemplate.collectionName, id, fieldName, newValue);
		} else {
			throw new Error("id is required.");
		}
	}

	static updateFields = async (id, updatedFields) => {
		if (isStringNotEmpty(id)) {
			return await updateFields(EmailTemplate.collectionName, id, updatedFields);
		} else {
			throw new Error("id is required.");
		}
	}

	static update = async (id, updatedEmailTemplate) => {
		const updatedObj = new EmailTemplate({ id: id, ...updatedEmailTemplate });
		return await update(EmailTemplate.collectionName, id, updatedObj.toDto());
	}

	static delete = async (id) => {
		if (isStringNotEmpty(id)) {
			return await remove(EmailTemplate.collectionName, id);
		} else {
			throw new Error("id is required.");
		}
	}

	static replaceInTemplate = (content, data, otherInfo = {}) => {
		return content.replace(
			/{{(\w*)}}/g,
			function (m, key) {
				return data.hasOwnProperty(key) ? data[key] : (CONSTANTS.hasOwnProperty(key) ? CONSTANTS[key] : (isObjectNotEmpty(otherInfo) && otherInfo.hasOwnProperty(key) ? otherInfo[key] : ""));
			}
		);
	}

	static replaceValueInTemplate = (content, placeholder, value) => {
		if (isStringNotEmpty(value)) {
			return content.replace(placeholder, value);
		}

		return content;
	}

	static getTestPayload = (emailTemplate, toEmail = 'test@example.com') => {
		return getTestPayloadByType(isStringNotEmpty(emailTemplate?.type) ? emailTemplate.type : 'USER', toEmail);
	}

	static renderTestEmail = (emailTemplate, toEmail = 'test@example.com') => {
		const { contact, otherInfo } = this.getTestPayload(emailTemplate, toEmail);

		return {
			contact,
			otherInfo,
			subject: this.replaceInTemplate(emailTemplate?.subject ?? '', contact, otherInfo),
			header: this.replaceInTemplate(emailTemplate?.header ?? '', contact, otherInfo),
			message: this.replaceInTemplate(emailTemplate?.message ?? '', contact, otherInfo),
		};
	}

	static sendTestEmail = async (emailTemplate, toEmail) => {
		if (isObjectNotEmpty(emailTemplate) && isStringNotEmpty(toEmail)) {
			const { contact, subject, header, message } = this.renderTestEmail(emailTemplate, toEmail);
			const name = isStringNotEmpty(contact?.firstName) && isStringNotEmpty(contact?.lastName) ? `${contact.firstName} ${contact.lastName}` : contact?.firstName ?? null;
			const response = await sendTemplateEmail(emailTemplate, null, toEmail, name, subject, header, message, emailTemplate.fromEmail, emailTemplate.fromName, emailTemplate.replyToEmail, emailTemplate.replyToName, { isTest: true });

			return {
				...response,
				subject,
				header,
				message,
			};
		}

		return { success: false, messageId: null, subject: null, header: null, message: null };
	}

	static sendEmail = async (emailTemplate, userId, contact, otherInfo = {}) => {
		if (isObjectNotEmpty(emailTemplate) && isStringNotEmpty(userId) && isObjectNotEmpty(contact) && isStringNotEmpty(contact.email)) {
			let subject = this.replaceInTemplate(emailTemplate.subject, contact, otherInfo);
			let header = this.replaceInTemplate(emailTemplate.header, contact, otherInfo);
			let message = this.replaceInTemplate(emailTemplate.message, contact, otherInfo);
			let { success, messageId } = await sendTemplateEmail(emailTemplate, userId, contact.email, isStringNotEmpty(contact.firstName) ? contact.firstName : null, subject, header, message, emailTemplate.fromEmail, emailTemplate.fromName, emailTemplate.replyToEmail, emailTemplate.replyToName);

			let snapshot = `Subject: ${subject}, Header: ${header}, Message: ${message}`;
			return { snapshot: snapshot, success: success, messageId: messageId };
		}

		return { snapshot: null, success: false, messageId: null };
	}

	static sendEmailById = async (templateId, userId, contact, otherInfo = {}) => {
		if (isStringNotEmpty(templateId) && isStringNotEmpty(userId) && isObjectNotEmpty(contact) && isStringNotEmpty(contact.email)) {
			try {
				const emailTemplate = await this.getById(templateId);
				if (isObjectNotEmpty(emailTemplate)) {
					return await this.sendEmail(emailTemplate, userId, contact, otherInfo);
				}
			} catch (error) {
				console.log(error);
			}
		}

		return { snapshot: null, success: false, messageId: null };
	}

	static sendEmailToLead = async (templateId, lead, otherInfo = {}) => {
		if (isStringNotEmpty(templateId) && isObjectNotEmpty(lead)) {
			let updatedLead = { ...lead };
			try {
				const programs = await Program.getAll();
				const program = isStringNotEmpty(lead?.programId) ? programs?.find(i => i.id === lead?.programId) : null;

				const emailTemplate = await this.getById(templateId);
				if (isObjectNotEmpty(emailTemplate)) {
					let existingSentEmails = isListNotEmpty(lead.sentEmails) ? [...lead.sentEmails] : [];
					if (isBooleanTrue(emailTemplate.sendMultiple) || !existingSentEmails.includes(emailTemplate.id)) {
						const link = buildLeadFunnelLink({ programSlug: program?.slug ?? null, lead });
						let emailRes = await this.sendEmail(emailTemplate, lead?.userId, lead, { ...otherInfo, leadId: lead?.id, link });
						if (isBooleanTrue(emailRes.success)) {
							let newActivity = await new Activity({
								userId: lead?.userId,
								type: 'EMAIL',
								direction: 'OUTGOING',
								details: emailRes.snapshot,
								outcome: 'SENT',
								important: false,
								messageId: null,
								callId: null,
								emailId: emailRes?.messageId,
								templateId: templateId
							}).create();

							let success2 = await Lead.updateField(lead.id, 'sentEmails', [...existingSentEmails, emailTemplate.id]);
							if (isBooleanTrue(success2)) {
								updatedLead.sentEmails = [...existingSentEmails, emailTemplate.id];
							}

							return updatedLead;
						}
					} else {
						return updatedLead;
					}
				}
			} catch (error) {
				console.log(error);
			}
		}

		return null;
	}

	static sendEmailToLeads = async (templateId, leads, setSuccessCount, setSkippedCount, setIsLoading, setError, updateDataHandler, otherInfo = {}) => {
		if (isStringNotEmpty(templateId) && isListNotEmpty(leads)) {
			try {
				setError(null);
				setIsLoading(true);
				const programs = await Program.getAll();

				const emailTemplate = await this.getById(templateId);
				if (isObjectNotEmpty(emailTemplate)) {
					let res = await PromisePool
						.for(leads)
						.withConcurrency(40)
						.process(async (lead, index, pool) => {
							let updatedLead = { ...lead };
							const program = isStringNotEmpty(lead?.programId) ? programs?.find(i => i.id === lead?.programId) : null;

							let existingSentEmails = isListNotEmpty(lead.sentEmails) ? [...lead.sentEmails] : [];
							if (isBooleanTrue(emailTemplate.sendMultiple) || !existingSentEmails.includes(emailTemplate.id)) {
								const link = buildLeadFunnelLink({ programSlug: program?.slug ?? null, lead });
								let emailRes = await this.sendEmail(emailTemplate, lead?.userId, lead, { ...otherInfo, leadId: lead?.id, link });
								if (isBooleanTrue(emailRes.success)) {
									let newActivity = await new Activity({
										userId: lead?.userId,
										type: 'EMAIL',
										direction: 'OUTGOING',
										details: emailRes.snapshot,
										outcome: 'SENT',
										important: false,
										messageId: null,
										callId: null,
										emailId: emailRes?.messageId,
										templateId: templateId
									}).create();

									let success2 = await Lead.updateField(lead.id, 'sentEmails', [...existingSentEmails, emailTemplate.id]);
									if (isBooleanTrue(success2)) {
										updatedLead.sentEmails = [...existingSentEmails, emailTemplate.id];

										updateDataHandler({ ...lead, ...updatedLead });
									}
									setSuccessCount(old => old + 1);
								}
							} else {
								setSkippedCount(old => old + 1);
							}

							return updatedLead;
						}).finally(() => {
							setIsLoading(false);
						});

					return res.results;
				}
			} catch (error) {
				console.log(error);
				setError(error);
				setIsLoading(false);
			}
		}

		return leads;
	}

	static sendEmailToUser = async (templateId, user, userId, coveredEntityId, otherInfo = {}) => {
		if (isStringNotEmpty(templateId) && isObjectNotEmpty(user)) {
			let updatedUser = { ...user };
			try {
				const emailTemplate = await this.getById(templateId);
				if (isObjectNotEmpty(emailTemplate)) {
					let existingActivities = await Activity.getByCoveredEntityIdAndUserIdAndType(coveredEntityId, userId, 'EMAIL');
					if (isBooleanTrue(emailTemplate.sendMultiple) || !existingActivities.some(i => i.templateId === emailTemplate.id)) {
						let emailRes = await this.sendEmail(emailTemplate, userId, user, otherInfo);
						if (isBooleanTrue(emailRes.success)) {
							let newActivity = await new Activity({
								coveredEntityId: coveredEntityId,
								userId: userId,
								type: 'EMAIL',
								direction: 'OUTGOING',
								details: emailRes.snapshot,
								outcome: 'SENT',
								important: false,
								messageId: null,
								callId: null,
								emailId: emailRes?.messageId,
								templateId: templateId
							}).create();

							if (isObjectNotEmpty(newActivity)) {
								updatedUser.emailCount = user.emailCount + 1;

								return { user: updatedUser, activity: newActivity };
							}
						}
					}
				}
			} catch (error) {
				console.log(error);
			}

			return { user: updatedUser, activity: null };
		}

		return { user: null, activity: null };
	}

	static sendEmailToUserId = async (templateId, userId, coveredEntityId, otherInfo = {}) => {
		if (isStringNotEmpty(templateId) && isStringNotEmpty(userId)) {
			try {
				let user = await User.getById(userId);
				const emailTemplate = await this.getById(templateId);
				if (isObjectNotEmpty(emailTemplate) && isObjectNotEmpty(user)) {
					let existingActivities = await Activity.getByCoveredEntityIdAndUserIdAndType(coveredEntityId, userId, 'EMAIL');
					if (isBooleanTrue(emailTemplate.sendMultiple) || !existingActivities.some(i => i.templateId === emailTemplate.id)) {
						let emailRes = await this.sendEmail(emailTemplate, userId, user, otherInfo);
						if (isBooleanTrue(emailRes.success)) {
							let newActivity = await new Activity({
								coveredEntityId: coveredEntityId,
								userId: userId,
								type: 'EMAIL',
								direction: 'OUTGOING',
								details: emailRes.snapshot,
								outcome: 'SENT',
								important: false,
								messageId: null,
								callId: null,
								emailId: emailRes?.messageId,
								templateId: templateId
							}).create();

							if (isObjectNotEmpty(newActivity)) {
								return { user: { ...user, emailCount: user.emailCount + 1 }, activity: newActivity };
							}
						}
					}
				}

				return { user: user, activity: null };
			} catch (error) {
				console.log(error);
			}
		}

		return { user: null, activity: null };
	}

	static sendEmailToUsers = async (templateId, users, coveredEntityId, setSuccessCount, setSkippedCount, setIsLoading, setError, updateDataHandler, otherInfo = {}) => {
		if (isStringNotEmpty(templateId) && isListNotEmpty(users)) {
			try {
				setError(null);
				setIsLoading(true);
				const emailTemplate = await this.getById(templateId);
				if (isObjectNotEmpty(emailTemplate)) {
					let res = await PromisePool
						.for(users)
						.withConcurrency(40)
						.process(async (user, index, pool) => {
							let updatedUser = { ...user };
							let existingActivities = await Activity.getByCoveredEntityIdAndUserIdAndType(coveredEntityId, user.userId, 'EMAIL');
							if (isBooleanTrue(emailTemplate.sendMultiple) || !existingActivities.some(i => i.templateId === emailTemplate.id)) {
								let emailRes = await this.sendEmail(emailTemplate, user?.userId, user, otherInfo);
								if (isBooleanTrue(emailRes.success)) {
									let newActivity = await new Activity({
										coveredEntityId: coveredEntityId,
										userId: user.userId,
										type: 'EMAIL',
										direction: 'OUTGOING',
										details: emailRes.snapshot,
										outcome: 'SENT',
										important: false,
										messageId: null,
										callId: null,
										emailId: emailRes?.messageId,
										templateId: templateId
									}).create();

									if (isObjectNotEmpty(newActivity)) {
										updatedUser.emailCount = user.emailCount + 1;

										updateDataHandler({ ...updatedUser });
									}

									setSuccessCount(old => old + 1);
								}
							} else {
								setSkippedCount(old => old + 1);
							}

							return updatedUser;
						}).finally(() => {
							setIsLoading(false);
						});

					return res.results;
				}
			} catch (error) {
				console.log(error);
				setError(error);
				setIsLoading(false);
			}
		}

		return users;
	}

	static sendEmailToUsersWithFilters = async (templateId, queryParameters, coveredEntityId, setSuccessCount, setSkippedCount, setIsLoading, setError, updateDataHandler, otherInfo = {}) => {
		if (!isStringNotEmpty(templateId)) {
			return false;
		}

		const canSetError = typeof setError === 'function';
		const canSetIsLoading = typeof setIsLoading === 'function';
		const canSetSuccess = typeof setSuccessCount === 'function';
		const canSetSkipped = typeof setSkippedCount === 'function';
		const canUpdateData = typeof updateDataHandler === 'function';

		try {
			if (canSetError) {
				setError(null);
			}
			if (canSetIsLoading) {
				setIsLoading(true);
			}

			const emailTemplate = await this.getById(templateId);
			if (!isObjectNotEmpty(emailTemplate)) {
				return false;
			}

			const pageSize = 500;
			let next = null;

			while (true) {
				const list = await User.getPaginated(queryParameters, pageSize, null, next);
				if (!isListNotEmpty(list)) {
					break;
				}

				await PromisePool
					.for(list)
					.withConcurrency(40)
					.process(async (user) => {
						// an absent flag is allowed, so only an explicit opt-out skips
						if (isBooleanFalse(user?.communicationPreferences?.marketingEmails) || isBooleanTrue(user?.communicationPreferences?.marketingOptOut)) {
							if (canSetSkipped) {
								setSkippedCount(old => old + 1);
							}
							return;
						}

						let existingActivities = await Activity.getByCoveredEntityIdAndUserIdAndType(coveredEntityId, user.userId, 'EMAIL');
						if (isBooleanTrue(emailTemplate.sendMultiple) || !existingActivities.some(i => i.templateId === emailTemplate.id)) {
							let emailRes = await this.sendEmail(emailTemplate, user?.userId, user, otherInfo);
							if (isBooleanTrue(emailRes.success)) {
								let newActivity = await new Activity({
									coveredEntityId: coveredEntityId,
									userId: user.userId,
									type: 'EMAIL',
									direction: 'OUTGOING',
									details: emailRes.snapshot,
									outcome: 'SENT',
									important: false,
									messageId: null,
									callId: null,
									emailId: emailRes?.messageId,
									templateId: templateId
								}).create();

								if (isObjectNotEmpty(newActivity)) {
									const updatedUser = { ...user, emailCount: (user.emailCount || 0) + 1 };

									if (canUpdateData) {
										updateDataHandler(updatedUser);
									}
								}

								if (canSetSuccess) {
									setSuccessCount(old => old + 1);
								}
							}
						} else if (canSetSkipped) {
							setSkippedCount(old => old + 1);
						}
					});

				next = list[list.length - 1];
				if (list.length < pageSize) {
					break;
				}
			}

			return true;
		} catch (error) {
			console.log(error);
			if (canSetError) {
				setError(error);
			}
			return false;
		} finally {
			if (canSetIsLoading) {
				setIsLoading(false);
			}
		}
	}

	static sendOrderEmailToUserId = async (templateId, userId, order, otherInfo = {}) => {
		if (isStringNotEmpty(templateId) && isObjectNotEmpty(order)) {
			let updatedOrder = { ...order };
			try {
				let user = await User.getById(userId);
				const emailTemplate = await this.getById(templateId);
				if (isObjectNotEmpty(emailTemplate)) {
					let existingSentEmails = isListNotEmpty(order.sentEmails) ? [...order.sentEmails] : [];
					if (isBooleanTrue(emailTemplate.sendMultiple) || !existingSentEmails.includes(emailTemplate.id)) {
						let emailRes = await this.sendEmail(emailTemplate, userId, user, { ...order, ...otherInfo });
						if (isBooleanTrue(emailRes.success)) {
							let success2 = await Order.updateField(order.id, 'sentEmails', [...existingSentEmails, emailTemplate.id]);
							if (isBooleanTrue(success2)) {
								let newActivity = await new Activity({
									coveredEntityId: order.coveredEntityId,
									userId: order.userId,
									type: 'EMAIL',
									direction: 'OUTGOING',
									details: emailRes.snapshot,
									outcome: 'SENT',
									important: false,
									messageId: null,
									callId: null,
									emailId: emailRes?.messageId,
									templateId: templateId
								}).create();

								if (isObjectNotEmpty(newActivity)) {
									updatedOrder.sentEmails = [...existingSentEmails, emailTemplate.id];
								}

								return updatedOrder;
							}
						}
					}
				}
			} catch (error) {
				console.log(error);
			}
		}

		return null;
	}

	static sendOrderEmailToUser = async (templateId, customer, order, coveredEntityId, otherInfo = {}) => {
		if (isStringNotEmpty(templateId) && isObjectNotEmpty(customer) && isObjectNotEmpty(order)) {
			let updatedOrder = { ...order };
			try {
				const emailTemplate = await this.getById(templateId);
				if (isObjectNotEmpty(emailTemplate)) {
					let existingSentEmails = isListNotEmpty(order.sentEmails) ? [...order.sentEmails] : [];
					if (isBooleanTrue(emailTemplate.sendMultiple) || !existingSentEmails.includes(emailTemplate.id)) {
						let emailRes = await this.sendEmail(emailTemplate, order?.userId, customer, { ...order, ...otherInfo });
						if (isBooleanTrue(emailRes.success)) {
							let success2 = await Order.updateField(order.id, 'sentEmails', [...existingSentEmails, emailTemplate.id]);
							if (isBooleanTrue(success2)) {
								let newActivity = await new Activity({
									coveredEntityId: order.coveredEntityId,
									userId: order.userId,
									type: 'EMAIL',
									direction: 'OUTGOING',
									details: emailRes.snapshot,
									outcome: 'SENT',
									important: false,
									messageId: null,
									callId: null,
									emailId: emailRes?.messageId,
									templateId: templateId
								}).create();

								if (isObjectNotEmpty(newActivity)) {
									updatedOrder.sentEmails = [...existingSentEmails, emailTemplate.id];
								}

								return updatedOrder;
							}
						}
					}
				}
			} catch (error) {
				console.log(error);
			}
		}

		return null;
	}

	static sendOrderEmailToUsers = async (templateId, orders, setSuccessCount, setSkippedCount, setIsLoading, setError, updateDataHandler, otherInfo = {}) => {
		if (isStringNotEmpty(templateId) && isListNotEmpty(orders)) {
			try {
				setError(null);
				setIsLoading(true);
				const emailTemplate = await this.getById(templateId);
				if (isObjectNotEmpty(emailTemplate)) {
					let res = await PromisePool
						.for(orders)
						.withConcurrency(40)
						.process(async (order, index, pool) => {
							let updatedOrder = { ...order };

							let existingSentEmails = isListNotEmpty(order.sentEmails) ? [...order.sentEmails] : [];
							if (isBooleanTrue(emailTemplate.sendMultiple) || !existingSentEmails.includes(emailTemplate.id)) {
								let emailRes = await this.sendEmail(emailTemplate, order?.userId, order.customer, { ...order, ...otherInfo });
								if (isBooleanTrue(emailRes.success)) {
									let success2 = await Order.updateField(order.id, 'sentEmails', [...existingSentEmails, emailTemplate.id]);
									if (isBooleanTrue(success2)) {
										let newActivity = await new Activity({
											coveredEntityId: order.coveredEntityId,
											userId: order.userId,
											type: 'EMAIL',
											direction: 'OUTGOING',
											details: emailRes.snapshot,
											outcome: 'SENT',
											important: false,
											messageId: null,
											callId: null,
											emailId: emailRes?.messageId,
											templateId: templateId
										}).create();

										if (isObjectNotEmpty(newActivity)) {
											updatedOrder.sentEmails = [...existingSentEmails, emailTemplate.id];

											updateDataHandler({ ...updatedOrder });
										}

										setSuccessCount(old => old + 1);
									}
								}
							} else {
								setSkippedCount(old => old + 1);
							}

							return updatedOrder;
						}).finally(() => {
							setIsLoading(false);
						});

					return res.results;
				}
			} catch (error) {
				console.log(error);
				setError(error);
				setIsLoading(false);
			}
		}

		return orders;
	}

	static sendOrderGroupEmailToUserId = async (templateId, userId, orderGroup, otherInfo = {}) => {
		if (isStringNotEmpty(templateId) && isObjectNotEmpty(orderGroup)) {
			let updatedOrderGroup = { ...orderGroup };
			try {
				let user = await User.getById(userId);
				const emailTemplate = await this.getById(templateId);
				if (isObjectNotEmpty(emailTemplate)) {
					let existingSentEmails = isListNotEmpty(orderGroup.sentEmails) ? [...orderGroup.sentEmails] : [];
					if (isBooleanTrue(emailTemplate.sendMultiple) || !existingSentEmails.includes(emailTemplate.id)) {
						let emailRes = await this.sendEmail(emailTemplate, userId, user, { ...orderGroup, ...otherInfo });
						if (isBooleanTrue(emailRes.success)) {
							let success2 = await OrderGroup.updateField(orderGroup.id, 'sentEmails', [...existingSentEmails, emailTemplate.id]);
							if (isBooleanTrue(success2)) {
								let newActivity = await new Activity({
									coveredEntityId: emailTemplate.coveredEntityId !== 'ALL' ? emailTemplate.coveredEntityId : null,
									userId: orderGroup.userId,
									type: 'EMAIL',
									direction: 'OUTGOING',
									details: emailRes.snapshot,
									outcome: 'SENT',
									important: false,
									messageId: null,
									callId: null,
									emailId: emailRes?.messageId,
									templateId: templateId
								}).create();

								if (isObjectNotEmpty(newActivity)) {
									updatedOrderGroup.sentEmails = [...existingSentEmails, emailTemplate.id];
								}

								return updatedOrderGroup;
							}
						}
					}
				}
			} catch (error) {
				console.log(error);
			}
		}

		return null;
	}

	static sendOrderEmailToUser = async (templateId, customer, orderGroup, otherInfo = {}) => {
		if (isStringNotEmpty(templateId) && isObjectNotEmpty(customer) && isObjectNotEmpty(orderGroup)) {
			let updatedOrderGroup = { ...orderGroup };
			try {
				const emailTemplate = await this.getById(templateId);
				if (isObjectNotEmpty(emailTemplate)) {
					let existingSentEmails = isListNotEmpty(orderGroup.sentEmails) ? [...orderGroup.sentEmails] : [];
					if (isBooleanTrue(emailTemplate.sendMultiple) || !existingSentEmails.includes(emailTemplate.id)) {
						let emailRes = await this.sendEmail(emailTemplate, orderGroup?.userId, customer, { ...orderGroup, ...otherInfo });
						if (isBooleanTrue(emailRes.success)) {
							let success2 = await OrderGroup.updateField(orderGroup.id, 'sentEmails', [...existingSentEmails, emailTemplate.id]);
							if (isBooleanTrue(success2)) {
								let newActivity = await new Activity({
									coveredEntityId: emailTemplate.coveredEntityId !== 'ALL' ? emailTemplate.coveredEntityId : null,
									userId: orderGroup.userId,
									type: 'EMAIL',
									direction: 'OUTGOING',
									details: emailRes.snapshot,
									outcome: 'SENT',
									important: false,
									messageId: null,
									callId: null,
									emailId: emailRes?.messageId,
									templateId: templateId
								}).create();

								if (isObjectNotEmpty(newActivity)) {
									updatedOrderGroup.sentEmails = [...existingSentEmails, emailTemplate.id];
								}

								return updatedOrderGroup;
							}
						}
					}
				}
			} catch (error) {
				console.log(error);
			}
		}

		return null;
	}

	static sendOrderEmailToUsers = async (templateId, orderGroups, setSuccessCount, setSkippedCount, setIsLoading, setError, updateDataHandler, otherInfo = {}) => {
		if (isStringNotEmpty(templateId) && isListNotEmpty(orderGroups)) {
			try {
				setError(null);
				setIsLoading(true);
				const emailTemplate = await this.getById(templateId);
				if (isObjectNotEmpty(emailTemplate)) {
					let res = await PromisePool
						.for(orderGroups)
						.withConcurrency(40)
						.process(async (orderGroup, index, pool) => {
							let updatedOrderGroup = { ...orderGroup };

							let existingSentEmails = isListNotEmpty(orderGroup.sentEmails) ? [...orderGroup.sentEmails] : [];
							if (isBooleanTrue(emailTemplate.sendMultiple) || !existingSentEmails.includes(emailTemplate.id)) {
								let emailRes = await this.sendEmail(emailTemplate, orderGroup?.userId, orderGroup.customer, { ...orderGroup, ...otherInfo });
								if (isBooleanTrue(emailRes.success)) {
									let success2 = await OrderGroup.updateField(orderGroup.id, 'sentEmails', [...existingSentEmails, emailTemplate.id]);
									if (isBooleanTrue(success2)) {
										let newActivity = await new Activity({
											coveredEntityId: emailTemplate.coveredEntityId !== 'ALL' ? emailTemplate.coveredEntityId : null,
											userId: orderGroup.userId,
											type: 'EMAIL',
											direction: 'OUTGOING',
											details: emailRes.snapshot,
											outcome: 'SENT',
											important: false,
											messageId: null,
											callId: null,
											emailId: emailRes?.messageId,
											templateId: templateId
										}).create();

										if (isObjectNotEmpty(newActivity)) {
											updatedOrderGroup.sentEmails = [...existingSentEmails, emailTemplate.id];

											updateDataHandler({ ...updatedOrderGroup });
										}

										setSuccessCount(old => old + 1);
									}
								}
							} else {
								setSkippedCount(old => old + 1);
							}

							return updatedOrderGroup;
						}).finally(() => {
							setIsLoading(false);
						});

					return res.results;
				}
			} catch (error) {
				console.log(error);
				setError(error);
				setIsLoading(false);
			}
		}

		return orderGroups;
	}

	static sendSubscriptionEmailToUser = async (templateId, customer, subscription, coveredEntityId, otherInfo = {}) => {
		if (isStringNotEmpty(templateId) && isObjectNotEmpty(customer) && isObjectNotEmpty(subscription)) {
			let updatedSubscription = { ...subscription };
			try {
				const emailTemplate = await this.getById(templateId);
				if (isObjectNotEmpty(emailTemplate)) {
					let existingSentEmails = isListNotEmpty(subscription.sentEmails) ? [...subscription.sentEmails] : [];
					if (isBooleanTrue(emailTemplate.sendMultiple) || !existingSentEmails.includes(emailTemplate.id)) {
						let emailRes = await this.sendEmail(emailTemplate, subscription?.userId, customer, { ...subscription, ...otherInfo });
						if (isBooleanTrue(emailRes.success)) {
							let updatedSubscriptionFields = {
								sentEmails: [...existingSentEmails, emailTemplate.id]
							};

							if (templateId === 'SUBSCRIPTION_CANCELLED') {
								updatedSubscriptionFields.cancelledEmailSent = true;
							}
							let success2 = await Subscription.updateFields(subscription.id, updatedSubscriptionFields);
							if (isBooleanTrue(success2)) {
								await new Activity({
									coveredEntityId: subscription.coveredEntityId,
									userId: subscription.userId,
									type: 'EMAIL',
									direction: 'OUTGOING',
									details: emailRes.snapshot,
									outcome: 'SENT',
									important: false,
									messageId: null,
									callId: null,
									emailId: emailRes?.messageId,
									templateId: templateId
								}).create();

								return { ...updatedSubscription, ...updatedSubscriptionFields };
							}
						}
					}
				}
			} catch (error) {
				console.log(error);
			}
		}

		return null;
	}

	static sendSubscriptionEmailToUsers = async (templateId, subscriptions, setSuccessCount, setSkippedCount, setIsLoading, setError, updateDataHandler, otherInfo = {}) => {
		if (isStringNotEmpty(templateId) && isListNotEmpty(subscriptions)) {
			try {
				setError(null);
				setIsLoading(true);
				const emailTemplate = await this.getById(templateId);
				if (isObjectNotEmpty(emailTemplate)) {
					let res = await PromisePool
						.for(subscriptions)
						.withConcurrency(40)
						.process(async (subscription, index, pool) => {
							let updatedSubscription = { ...subscription };

							let existingSentEmails = isListNotEmpty(subscription.sentEmails) ? [...subscription.sentEmails] : [];
							if (isBooleanTrue(emailTemplate.sendMultiple) || !existingSentEmails.includes(emailTemplate.id)) {
								let emailRes = await this.sendEmail(emailTemplate, subscription?.userId, subscription.customer, { ...subscription, ...otherInfo });
								if (isBooleanTrue(emailRes.success)) {
									let updatedSubscriptionFields = {
										sentEmails: [...existingSentEmails, emailTemplate.id]
									};

									if (templateId === 'SUBSCRIPTION_CANCELLED') {
										updatedSubscriptionFields.cancelledEmailSent = true;
									}

									let success2 = await Subscription.updateFields(subscription.id, updatedSubscriptionFields);
									if (isBooleanTrue(success2)) {
										await new Activity({
											coveredEntityId: subscription.coveredEntityId,
											userId: subscription.userId,
											type: 'EMAIL',
											direction: 'OUTGOING',
											details: emailRes.snapshot,
											outcome: 'SENT',
											important: false,
											messageId: null,
											callId: null,
											emailId: emailRes?.messageId,
											templateId: templateId
										}).create();

										setSuccessCount(old => old + 1);
										updateDataHandler({ ...updatedSubscription, ...updatedSubscriptionFields });

										return { ...updatedSubscription, ...updatedSubscriptionFields };
									}
								}
							} else {
								setSkippedCount(old => old + 1);
							}

							return updatedSubscription;
						}).finally(() => {
							setIsLoading(false);
						});

					return res.results;
				}
			} catch (error) {
				console.log(error);
				setError(error);
				setIsLoading(false);
			}
		}

		return subscriptions;
	}

	static getById = async (id) => {
		if (isStringNotEmpty(id)) {
			return await getById(EmailTemplate.collectionName, id);
		} else {
			throw new Error("id is required.");
		}
	}

	static getCount = async (queryParameters) => {
		let { coveredEntityId, type, marketing } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', 'in', [...coveredEntityId, 'ALL']));
		} else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', 'in', [coveredEntityId, 'ALL']));
		}

		if (isListNotEmpty(type)) {
			queryConstraints.push(where('type', 'in', type));
		} else if (isStringNotEmptyOrZeroLength(type)) {
			queryConstraints.push(where('type', '==', type));
		}

		if (isBooleanTrue(marketing)) {
			queryConstraints.push(where('marketing', '==', true));
		} else if (isBooleanFalse(marketing)) {
			queryConstraints.push(where('marketing', '==', false));
		}

		let q = query(collection(db, EmailTemplate.collectionName), ...queryConstraints);

		const snapShot = await getCountFromServer(q);

		//console.log(snapShot.data());

		return snapShot.data().count;
	}

	static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'orderedAt') => {
		let { coveredEntityId, type, marketing } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', 'in', [...coveredEntityId, 'ALL']));
		} else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', 'in', [coveredEntityId, 'ALL']));
		}

		if (isListNotEmpty(type)) {
			queryConstraints.push(where('type', 'in', type));
		} else if (isStringNotEmptyOrZeroLength(type)) {
			queryConstraints.push(where('type', '==', type));
		}

		if (isBooleanTrue(marketing)) {
			queryConstraints.push(where('marketing', '==', true));
		} else if (isBooleanFalse(marketing)) {
			queryConstraints.push(where('marketing', '==', false));
		}

		let q = null;
		if (isObjectNotEmpty(firstVisible)) {
			q = query(collection(db, EmailTemplate.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
		} else if (isObjectNotEmpty(lastVisible)) {
			q = query(collection(db, EmailTemplate.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
		} else {
			q = query(collection(db, EmailTemplate.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
		}

		let snapshot = await getDocs(q);
		return snapshot.docs.map((doc) => {
			return {
				id: doc.id,
				...doc.data()
			};
		});
	}

	static get = async (queryParameters) => {
		let allRecords = [];
		try {
			let next = null;

			let totalCount = await this.getCount(queryParameters);
			let totalPages = Math.ceil(totalCount / 10000.0);
			// console.log('totalCount: ' + totalCount);
			// console.log('totalPages: ' + totalPages);
			let pages = [];
			for (let i = 0; i < totalPages; i++) {
				pages.push(i + 1);
			}
			for (const page of pages) {
				// console.log('starting page ' + page);

				let list = await this.getPaginated(queryParameters, 10000, null, next, 'name');

				// console.log(list.length);
				allRecords = [...allRecords, ...list];

				// console.log(allRecords.length);

				// console.log('go to next page');
				next = list[list.length - 1];
			}
		} catch (error) {
			console.log(error);
		}

		return allRecords;
	}
}

export default EmailTemplate;
